import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const emptyService = {
  id: "",
  image: [],
  heading: "",
  text: "",
};

const AdditionalServiceModal = ({
  showModal,
  selectedService,
  previewImage,
  handleChange,
  handleImageChange,
  handleSubmit,
  closeModal,
  submitting,
  modalMode,
  message,
  error,
}) => {
  if (!showModal || !selectedService) return null;

  return createPortal(
    <div style={styles.modalOverlay} onClick={closeModal}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {modalMode === "add" ? "Add Additional Service" : "Edit Additional Service"}
          </h3>

          <button type="button" onClick={closeModal} style={styles.closeBtn}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.imageBox}>
            <label style={styles.label}>Service Image</label>

            {previewImage ? (
              <img
                src={previewImage}
                alt="Additional Service Preview"
                style={styles.image}
              />
            ) : (
              <div style={styles.noImageBox}>No image selected</div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Service Heading</label>
            <input
              type="text"
              name="heading"
              value={selectedService.heading || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter service heading"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Service Text</label>
            <textarea
              name="text"
              value={selectedService.text || ""}
              onChange={handleChange}
              rows="5"
              style={styles.textarea}
              placeholder="Enter service text"
              required
            />
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.button} disabled={submitting}>
            {submitting
              ? "Saving..."
              : modalMode === "add"
              ? "Add Service"
              : "Update Service"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

const AdditionalServicesEditor = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit");

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/additional-service-list"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status && result.data) {
        const serviceData = Array.isArray(result.data)
          ? result.data
          : [result.data];

        setServices(serviceData);
      } else {
        throw new Error(result.message || "Failed to fetch additional services");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setSelectedService({ ...emptyService });
    setPreviewImage("");
    setMessage("");
    setError("");
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setModalMode("edit");
    setSelectedService({
      ...service,
      image: service.image ? [service.image] : [],
    });

    setPreviewImage(
      Array.isArray(service.image) ? service.image[0] || "" : service.image || ""
    );

    setMessage("");
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedService(null);
    setPreviewImage("");
    setMessage("");
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSelectedService((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);

      setSelectedService((prev) => ({
        ...prev,
        image: [file],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");
    setError("");

    try {
      const payload = new FormData();

      payload.append("heading", selectedService.heading || "");
      payload.append("text", selectedService.text || "");

      if (
        selectedService.image &&
        selectedService.image.length > 0 &&
        selectedService.image[0] instanceof File
      ) {
        payload.append("image", selectedService.image[0]);
      }

      let apiUrl = "https://eventscons.com/eventbackendnew/api/additional-service-add";

      if (modalMode === "edit") {
        apiUrl = "https://eventscons.com/eventbackendnew/api/additional-service-update";
        payload.append("id", selectedService.id);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        body: payload,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message ||
            (modalMode === "add"
              ? "Additional service add failed"
              : "Additional service update failed")
        );
      }

      setMessage(
        result.message ||
          (modalMode === "add"
            ? "Additional service added successfully"
            : "Additional service updated successfully")
      );

      await fetchServices();

      setTimeout(() => {
        closeModal();
      }, 800);
    } catch (err) {
      setError(err.message || "Something went wrong while saving");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this additional service?"
    );

    if (!confirmDelete) return;

    try {
      setDeletingId(id);
      setMessage("");
      setError("");

      const response = await fetch(
        `https://eventscons.com/eventbackendnew/api/additional-service-delete/${id}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Delete failed");
      }

      setMessage(result.message || "Additional service deleted successfully");
      await fetchServices();
    } catch (err) {
      setError(err.message || "Something went wrong while deleting");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p style={styles.loading}>Loading additional services...</p>;
  if (error && services.length === 0) return <p style={styles.error}>{error}</p>;

  return (
    <>
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.headerRow}>
            <div>
              <h2 style={styles.heading}>Additional Services</h2>
              <p style={styles.subHeading}>
                Manage additional services with add, edit, and delete actions.
              </p>
            </div>

            <button
              type="button"
              style={styles.addButton}
              onClick={openAddModal}
            >
              + Add New Service
            </button>
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          {services.length === 0 ? (
            <div style={styles.emptyBox}>
              <p style={styles.emptyText}>No additional services found.</p>
              <button
                type="button"
                style={styles.addButton}
                onClick={openAddModal}
              >
                + Add First Service
              </button>
            </div>
          ) : (
            <div style={styles.grid}>
              {services.map((service) => (
                <div key={service.id} style={styles.card}>
                  <div style={styles.cardImageWrap}>
                    <img
                      src={
                        Array.isArray(service.image)
                          ? service.image[0] || ""
                          : service.image || ""
                      }
                      alt={service.heading || "Additional Service"}
                      style={styles.cardImage}
                    />

                    <button
                      style={styles.editBtn}
                      onClick={() => openEditModal(service)}
                      type="button"
                      title="Edit Service"
                    >
                      ✏️
                    </button>
                  </div>

                  <div style={styles.cardBody}>
                    <h3 style={styles.cardTitle}>{service.heading}</h3>
                    <p style={styles.cardText}>{service.text}</p>

                    <button
                      type="button"
                      style={{
                        ...styles.deleteBtn,
                        opacity: deletingId === service.id ? 0.7 : 1,
                      }}
                      onClick={() => handleDelete(service.id)}
                      disabled={deletingId === service.id}
                    >
                      {deletingId === service.id ? "Deleting..." : "Delete Service"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <AdditionalServiceModal
        showModal={showModal}
        selectedService={selectedService}
        previewImage={previewImage}
        handleChange={handleChange}
        handleImageChange={handleImageChange}
        handleSubmit={handleSubmit}
        closeModal={closeModal}
        submitting={submitting}
        message={message}
        error={error}
        modalMode={modalMode}
      />
    </>
  );
};

const styles = {
  section: {
    padding: "40px 20px",
    border: "1px solid #eee",
    background: "#fff",
    margin: "30px",
    boxShadow: "0px 10px 25px #dcdcdc",
    borderRadius: "15px",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    marginBottom: "25px",
    flexWrap: "wrap",
  },
  heading: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#000",
    textAlign: "left",
  },
  subHeading: {
    margin: "8px 0 0 0",
    color: "#6b7280",
    fontSize: "15px",
    fontWeight: "500",
  },
  addButton: {
    padding: "12px 18px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "20px",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
    border: "1px solid #efefef",
  },
  cardImageWrap: {
    position: "relative",
  },
  cardImage: {
    width: "100%",
    height: "220px",
    objectFit: "contain",
    padding:"20px",
    display: "block",
    background: "#f5f5f5",
  },
  cardBody: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "700",
    color: "#222",
  },
  cardText: {
    margin: 0,
    fontSize: "14px",
    lineHeight: "1.7",
    color: "#5b5b5b",
    minHeight: "72px",
  },
  editBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "none",
    background: "#9B1321",
    color: "#fff",
    cursor: "pointer",
    fontSize: "18px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  },
  deleteBtn: {
    padding: "12px 16px",
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  emptyBox: {
    padding: "40px 20px",
    textAlign: "center",
    border: "1px dashed #d1d5db",
    borderRadius: "14px",
    background: "#fafafa",
  },
  emptyText: {
    margin: "0 0 16px 0",
    color: "#6b7280",
    fontWeight: "500",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 99999,
  },
  modal: {
    position: "relative",
    width: "100%",
    maxWidth: "820px",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  modalTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#222",
    margin: 0,
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "30px",
    cursor: "pointer",
    color: "#444",
    lineHeight: 1,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  imageBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  image: {
    width: "100%",
    maxWidth: "100%",
    height: "260px",
    objectFit: "cover",
    borderRadius: "10px",
    border: "1px solid #ddd",
  },
  noImageBox: {
    height: "180px",
    border: "1px dashed #bbb",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    background: "#fafafa",
    fontWeight: "500",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontWeight: "600",
    color: "#333",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    width: "100%",
    boxSizing: "border-box",
  },
  textarea: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    resize: "vertical",
    width: "100%",
    boxSizing: "border-box",
  },
  button: {
    padding: "14px 20px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  success: {
    color: "green",
    fontWeight: "600",
    marginBottom: "16px",
  },
  error: {
    color: "red",
    fontWeight: "600",
    marginBottom: "16px",
  },
  loading: {
    padding: "40px",
    textAlign: "center",
  },
};

export default AdditionalServicesEditor;